import { PDFDocument, rgb, PDFFont, PDFPage } from 'pdf-lib';
import { saveAs } from 'file-saver';

/**
 * PDF AI Editor - Advanced PDF editing capabilities
 */

// Add text to PDF at specific coordinates
export const addTextToPDF = async (file, text, x, y, options = {}) => {
  const arrayBuffer = await file.arrayBuffer();
  const pdfDoc = await PDFDocument.load(arrayBuffer);
  
  const pages = pdfDoc.getPages();
  const page = pages[options.pageIndex || 0];
  
  const fontSize = options.fontSize || 12;
  const color = options.color || rgb(0, 0, 0);
  
  page.drawText(text, {
    x: x,
    y: y,
    size: fontSize,
    color: color,
    font: await pdfDoc.embedFont('Helvetica'),
  });
  
  const pdfBytes = await pdfDoc.save();
  const blob = new Blob([pdfBytes], { type: 'application/pdf' });
  return { blob, filename: file.name.replace('.pdf', '_edited.pdf') };
};

// Add annotations (highlight, underline, etc.)
export const addAnnotation = async (file, annotationType, coordinates, options = {}) => {
  const arrayBuffer = await file.arrayBuffer();
  const pdfDoc = await PDFDocument.load(arrayBuffer);
  
  const pages = pdfDoc.getPages();
  const page = pages[options.pageIndex || 0];
  
  const { x, y, width, height } = coordinates;
  const color = options.color || rgb(1, 1, 0); // Yellow highlight by default
  
  switch (annotationType) {
    case 'highlight':
      page.drawRectangle({
        x: x,
        y: y,
        width: width,
        height: height,
        color: rgb(1, 1, 0),
        opacity: 0.3,
      });
      break;
    case 'underline':
      page.drawLine({
        start: { x: x, y: y },
        end: { x: x + width, y: y },
        thickness: 2,
        color: color,
      });
      break;
    case 'strikethrough':
      page.drawLine({
        start: { x: x, y: y + height / 2 },
        end: { x: x + width, y: y + height / 2 },
        thickness: 2,
        color: color,
      });
      break;
    case 'box':
      page.drawRectangle({
        x: x,
        y: y,
        width: width,
        height: height,
        borderColor: color,
        borderWidth: 2,
      });
      break;
  }
  
  const pdfBytes = await pdfDoc.save();
  const blob = new Blob([pdfBytes], { type: 'application/pdf' });
  return { blob, filename: file.name.replace('.pdf', '_annotated.pdf') };
};

// Fill form fields in PDF
export const fillPDFForm = async (file, formData, onProgress) => {
  onProgress(10);
  
  const arrayBuffer = await file.arrayBuffer();
  const pdfDoc = await PDFDocument.load(arrayBuffer);
  
  onProgress(30);
  
  const form = pdfDoc.getForm();
  const fields = form.getFields();
  
  onProgress(50);
  
  // Fill form fields
  fields.forEach((field, index) => {
    const fieldName = field.getName();
    if (formData[fieldName]) {
      try {
        if (field.constructor.name === 'PDFTextField') {
          field.setText(formData[fieldName]);
        } else if (field.constructor.name === 'PDFCheckBox') {
          if (formData[fieldName]) {
            field.check();
          }
        } else if (field.constructor.name === 'PDFDropdown') {
          field.select(formData[fieldName]);
        } else if (field.constructor.name === 'PDFRadioGroup') {
          field.select(formData[fieldName]);
        }
      } catch (error) {
        console.warn(`Could not fill field ${fieldName}:`, error);
      }
    }
    onProgress(50 + (index / fields.length) * 40);
  });
  
  onProgress(95);
  
  const pdfBytes = await pdfDoc.save();
  const blob = new Blob([pdfBytes], { type: 'application/pdf' });
  
  onProgress(100);
  
  return { blob, filename: file.name.replace('.pdf', '_filled.pdf') };
};

// Extract form fields from PDF
export const extractFormFields = async (file, onProgress) => {
  onProgress(10);
  
  const arrayBuffer = await file.arrayBuffer();
  const pdfDoc = await PDFDocument.load(arrayBuffer);
  
  onProgress(30);
  
  const form = pdfDoc.getForm();
  const fields = form.getFields();
  
  onProgress(50);
  
  const formData = {};
  fields.forEach((field, index) => {
    const fieldName = field.getName();
    const fieldType = field.constructor.name;
    
    try {
      let value = '';
      if (fieldType === 'PDFTextField') {
        value = field.getText();
      } else if (fieldType === 'PDFCheckBox') {
        value = field.isChecked();
      } else if (fieldType === 'PDFDropdown') {
        value = field.getSelected();
      } else if (fieldType === 'PDFRadioGroup') {
        value = field.getSelected();
      }
      
      formData[fieldName] = {
        type: fieldType,
        value: value,
        required: field.isRequired(),
      };
    } catch (error) {
      formData[fieldName] = {
        type: fieldType,
        value: null,
        error: error.message,
      };
    }
    
    onProgress(50 + (index / fields.length) * 40);
  });
  
  onProgress(100);
  
  return formData;
};

// Merge multiple PDFs
export const mergePDFs = async (files, onProgress) => {
  onProgress(0);
  
  const mergedPdf = await PDFDocument.create();
  
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await PDFDocument.load(arrayBuffer);
    
    const pages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
    pages.forEach((page) => {
      mergedPdf.addPage(page);
    });
    
    onProgress((i / files.length) * 90);
  }
  
  onProgress(95);
  
  const pdfBytes = await mergedPdf.save();
  const blob = new Blob([pdfBytes], { type: 'application/pdf' });
  
  onProgress(100);
  
  return { blob, filename: 'merged.pdf' };
};

// Split PDF into multiple files
export const splitPDF = async (file, splitOptions, onProgress) => {
  onProgress(0);
  
  const arrayBuffer = await file.arrayBuffer();
  const pdfDoc = await PDFDocument.load(arrayBuffer);
  
  onProgress(20);
  
  const totalPages = pdfDoc.getPageCount();
  const results = [];
  
  if (splitOptions.mode === 'page-range') {
    // Split by page ranges
    for (const range of splitOptions.ranges) {
      const newPdf = await PDFDocument.create();
      const [start, end] = range;
      
      for (let i = start - 1; i < Math.min(end, totalPages); i++) {
        const [copiedPage] = await newPdf.copyPages(pdfDoc, [i]);
        newPdf.addPage(copiedPage);
      }
      
      const pdfBytes = await newPdf.save();
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      results.push({
        blob,
        filename: `${file.name.replace('.pdf', '')}_pages_${start}-${end}.pdf`,
      });
    }
  } else if (splitOptions.mode === 'every-n-pages') {
    // Split every N pages
    const n = splitOptions.n || 1;
    let currentPdf = await PDFDocument.create();
    let pageCount = 0;
    let fileIndex = 1;
    
    for (let i = 0; i < totalPages; i++) {
      const [copiedPage] = await currentPdf.copyPages(pdfDoc, [i]);
      currentPdf.addPage(copiedPage);
      pageCount++;
      
      if (pageCount >= n || i === totalPages - 1) {
        const pdfBytes = await currentPdf.save();
        const blob = new Blob([pdfBytes], { type: 'application/pdf' });
        results.push({
          blob,
          filename: `${file.name.replace('.pdf', '')}_part_${fileIndex}.pdf`,
        });
        
        currentPdf = await PDFDocument.create();
        pageCount = 0;
        fileIndex++;
      }
      
      onProgress(20 + (i / totalPages) * 70);
    }
  }
  
  onProgress(100);
  
  return results;
};

// Rotate PDF pages
export const rotatePDFPages = async (file, rotation, pageIndices = null, onProgress) => {
  onProgress(10);
  
  const arrayBuffer = await file.arrayBuffer();
  const pdfDoc = await PDFDocument.load(arrayBuffer);
  
  onProgress(30);
  
  const pages = pdfDoc.getPages();
  const indicesToRotate = pageIndices || pages.map((_, i) => i);
  
  indicesToRotate.forEach((index, i) => {
    if (pages[index]) {
      pages[index].setRotation(rotation);
    }
    onProgress(30 + (i / indicesToRotate.length) * 60);
  });
  
  onProgress(95);
  
  const pdfBytes = await pdfDoc.save();
  const blob = new Blob([pdfBytes], { type: 'application/pdf' });
  
  onProgress(100);
  
  return { blob, filename: file.name.replace('.pdf', '_rotated.pdf') };
};

// Add watermark to PDF
export const addWatermark = async (file, watermarkText, options = {}, onProgress) => {
  onProgress(10);
  
  const arrayBuffer = await file.arrayBuffer();
  const pdfDoc = await PDFDocument.load(arrayBuffer);
  
  onProgress(30);
  
  const pages = pdfDoc.getPages();
  const font = await pdfDoc.embedFont('Helvetica');
  
  const fontSize = options.fontSize || 48;
  const color = options.color || rgb(0.7, 0.7, 0.7);
  const opacity = options.opacity || 0.3;
  const angle = options.angle || -45;
  
  pages.forEach((page, index) => {
    const { width, height } = page.getSize();
    const centerX = width / 2;
    const centerY = height / 2;
    
    page.drawText(watermarkText, {
      x: centerX,
      y: centerY,
      size: fontSize,
      color: color,
      opacity: opacity,
      font: font,
      rotate: { angleInDegrees: angle },
    });
    
    onProgress(30 + (index / pages.length) * 60);
  });
  
  onProgress(95);
  
  const pdfBytes = await pdfDoc.save();
  const blob = new Blob([pdfBytes], { type: 'application/pdf' });
  
  onProgress(100);
  
  return { blob, filename: file.name.replace('.pdf', '_watermarked.pdf') };
};

// Compress PDF
export const compressPDF = async (file, compressionLevel = 'medium', onProgress) => {
  onProgress(10);
  
  // Note: pdf-lib doesn't have built-in compression
  // This is a placeholder - in production, you'd use a server-side solution
  // or a library that supports compression
  
  const arrayBuffer = await file.arrayBuffer();
  const pdfDoc = await PDFDocument.load(arrayBuffer);
  
  onProgress(50);
  
  // Apply compression settings if available
  const pdfBytes = await pdfDoc.save({
    useObjectStreams: true,
    addDefaultPage: false,
  });
  
  onProgress(90);
  
  const blob = new Blob([pdfBytes], { type: 'application/pdf' });
  
  onProgress(100);
  
  return { blob, filename: file.name.replace('.pdf', '_compressed.pdf') };
};


